import {initContract} from '@ts-rest/core';
import {DB_SCHEMAS} from '../db/schemaExports';
import {z} from 'zod';

const c = initContract();

// ISO 8601 datetime string validation
const isoDateString = z.string().refine(
  (val) => {
    const date = new Date(val);
    return !isNaN(date.getTime());
  },
  {message: 'Invalid date format. Expected ISO 8601 date string (e.g., 2024-01-15T00:00:00Z)'}
);

// Response schema for usage summary
const usageSummarySchema = z.object({
  totalInputTokens: z.number(),
  totalOutputTokens: z.number(),
  totalTokens: z.number(),
  requestCount: z.number(),
  byOperationType: z.array(
    z.object({
      operationType: z.enum(['chat', 'title', 'summarization', 'keywords', 'image_description']),
      inputTokens: z.number(),
      outputTokens: z.number(),
      totalTokens: z.number(),
      requestCount: z.number()
    })
  ),
  byModel: z.array(
    z.object({
      model: z.string(),
      inputTokens: z.number(),
      outputTokens: z.number(),
      totalTokens: z.number(),
      requestCount: z.number()
    })
  )
});

export const tokenUsageContract = c.router(
  {
    // Get usage summary for the current user within a specific tenant
    getSummary: {
      method: 'GET',
      path: '/summary',
      query: z.object({
        tenantId: z.string().uuid({message: 'tenantId must be a valid UUID'}),
        startDate: isoDateString.optional(),
        endDate: isoDateString.optional()
      }),
      responses: {
        200: usageSummarySchema,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Get token usage summary for the current user within a tenant'
    },

    // Get usage for a specific conversation
    getByConversation: {
      method: 'GET',
      path: '/conversations/:conversationId',
      pathParams: z.object({
        conversationId: z.string().uuid()
      }),
      responses: {
        200: z.array(DB_SCHEMAS.selectTokenUsage),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Get token usage for a specific conversation'
    },

    // Get tenant-level usage (admin only)
    getTenantUsage: {
      method: 'GET',
      path: '/tenants/:tenantId',
      pathParams: z.object({
        tenantId: z.string().uuid()
      }),
      query: z.object({
        startDate: isoDateString.optional(),
        endDate: isoDateString.optional()
      }),
      responses: {
        200: usageSummarySchema,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Get token usage summary for a tenant (admin only)'
    }
  },
  {
    pathPrefix: '/usage'
  }
);
